// Define translation types
export type Language = 'fr' | 'en';

// Define all the translatable text for the application
export interface Translations {
  header: {
    home: string;
    about: string;
    services: string;
    gallery: string;
    contactBtn: string;
  };
  hero: {
    title: string;
    subtitle: string;
    services: string;
    experience: string;
    cta: string;
  };
  about: {
    title: string;
    history: {
      title: string;
      content: string;
    };
    team: {
      title: string;
      content: string;
    };
    commitment: {
      title: string;
      content: string;
    };
  };
  services: {
    title: string;
    items: {
      [key: string]: {
        title: string;
        description: string;
      };
    };
  };
  gallery: {
    title: string;
    noItems: string;
    categories: {
      all: string;
      excavation: string;
      landscaping: string;
      preparation: string;
      concrete: string;
    };
    items: {
      residentialExcavation: {
        title: string;
        description: string;
      };
      poolExcavation: {
        title: string;
        description: string;
      };
      sitePreperation: {
        title: string;
        description: string;
      };
      landscaping: {
        title: string;
        description: string;
      };
      concrete: {
        title: string;
        description: string;
      };
      memorialSpace: {
        title: string;
        description: string;
      };
    };
  };
  testimonials: {
    title: string;
    verified: string;
    writeReview: string;
  };
  contact: {
    title: string;
    subtitle: string;
    info: {
      title: string;
      address: {
        label: string;
        value: string;
      };
      phone: {
        label: string;
        value: string;
      };
      email: {
        label: string;
        value: string;
      };
      hours: {
        label: string;
        weekdays: string;
        saturday: string;
        sunday: string;
      };
    };
    form: {
      title: string;
      name: {
        label: string;
        placeholder: string;
      };
      email: {
        label: string;
        placeholder: string;
      };
      phone: {
        label: string;
        placeholder: string;
        optional: string;
      };
      address: {
        label: string;
        placeholder: string;
        optional: string;
      };
      service: {
        label: string;
        placeholder: string;
        optional: string;
      };
      message: {
        label: string;
        placeholder: string;
      };
      submit: string;
      submitting: string;
      success: string;
      error: string;
    };
  };
  footer: {
    about: string;
    quickLinks: string;
    ourServices: string;
    contactUs: string;
    copyright: string;
    privacy: string;
    terms: string;
  };
}

// French translations
export const frTranslations: Translations = {
  header: {
    home: 'Accueil',
    about: 'À propos',
    services: 'Services',
    gallery: 'Galerie',
    contactBtn: 'Nous joindre',
  },
  hero: {
    title: 'Vaudreuil Multiservices Inc.',
    subtitle: 'Terrassement • Excavation • Nivellement • Déneigement',
    services: 'Terrassement • Excavation • Nivellement • Déneigement',
    experience: '20+ ans d\'expérience • Projets résidentiels',
    cta: 'Demander un devis',
  },
  about: {
    title: 'À propos de nous',
    history: {
      title: 'Notre histoire',
      content: 'Fondée en 2022, Vaudreuil Multiservices est une entreprise familiale avec plus de 20 ans d\'expérience dans le domaine de l\'excavation et du terrassement.',
    },
    team: {
      title: 'Notre équipe',
      content: 'Notre équipe est composée de professionnels qualifiés ayant une grande expérience dans le domaine de la construction et de l\'aménagement paysager.',
    },
    commitment: {
      title: 'Notre engagement',
      content: 'Nous nous engageons à fournir des services de haute qualité, en respectant les délais et les budgets, pour assurer la satisfaction totale de nos clients.',
    },
  },
  services: {
    title: 'Nos services',
    items: {
      excavation: {
        title: 'Excavation',
        description: 'Services d\'excavation pour projets résidentiels et aménagements d\'espaces mémoriaux paysagers.',
      },
      landscaping: {
        title: 'Terrassement',
        description: 'Aménagement paysager et terrassement pour embellir votre propriété.',
      },
      leveling: {
        title: 'Nivellement',
        description: 'Services de nivellement de terrain pour une fondation stable.',
      },
      snowRemoval: {
        title: 'Déneigement',
        description: 'Services de déneigement résidentiel pendant l\'hiver.',
      },
      drainage: {
        title: 'Drainage',
        description: 'Solutions de drainage de terrain pour une meilleure gestion des eaux de surface et prévenir l\'accumulation d\'eau sur votre propriété.',
      },
      installation: {
        title: 'Installation',
        description: 'Installation professionnelle de dalles de béton pour stationnements, trottoirs et plus encore.',
      },
    },
  },
  gallery: {
    title: 'Galerie de projets',
    categories: {
      all: 'Tous',
      excavation: 'Excavation',
      landscaping: 'Terrassement',
      preparation: 'Préparation',
      concrete: 'Béton'
    },
    noItems: 'Aucun projet dans cette catégorie',
    items: {
      residentialExcavation: {
        title: 'Excavation résidentielle',
        description: 'Projet d\'excavation pour une nouvelle construction résidentielle à Vaudreuil.'
      },
      poolExcavation: {
        title: 'Excavation pour piscine',
        description: 'Préparation et excavation de terrain pour l\'installation d\'une piscine résidentielle.'
      },
      sitePreperation: {
        title: 'Préparation de site',
        description: 'Préparation complète d\'un site pour un nouveau développement multi-résidentiel.'
      },
      landscaping: {
        title: 'Aménagement paysager',
        description: 'Projet d\'aménagement complet pour une propriété de luxe à Hudson.'
      },
      concrete: {
        title: 'Travaux de béton',
        description: 'Coulage de fondation et travaux de béton pour un projet résidentiel.'
      },
      memorialSpace: {
        title: 'Aménagement d\'espace mémorial',
        description: 'Travaux d\'excavation et d\'aménagement pour un jardin mémorial paysager.'
      }
    }
  },
  testimonials: {
    title: 'Témoignages',
    verified: 'Client vérifié',
    writeReview: 'Laisser un témoignage'
  },
  contact: {
    title: 'Contactez-nous',
    subtitle: 'N\'hésitez pas à nous contacter pour toute question ou demande de devis.',
    info: {
      title: 'Informations de contact',
      address: {
        label: 'Adresse',
        value: '1772 rue de la Petite-Oasis, Québec, QC',
      },
      phone: {
        label: 'Téléphone',
        value: '(418) 907-9922',
      },
      email: {
        label: 'Email',
        value: 'vaudreuilmultiservices@gmail.com',
      },
      hours: {
        label: 'Heures d\'ouverture',
        weekdays: 'Lun - Ven: 8h00 - 17h00',
        saturday: 'Sam: 9h00 - 12h00',
        sunday: 'Dim: Fermé',
      },
    },
    form: {
      title: 'Envoyez-nous un message',
      name: {
        label: 'Nom complet',
        placeholder: 'Votre nom complet',
      },
      email: {
        label: 'Email',
        placeholder: 'Votre adresse email',
      },
      phone: {
        label: 'Téléphone',
        placeholder: 'Votre numéro de téléphone',
        optional: '(optionnel)',
      },
      address: {
        label: 'Adresse',
        placeholder: 'Votre adresse',
        optional: '(optionnel)',
      },
      service: {
        label: 'Service souhaité',
        placeholder: 'Sélectionnez un service',
        optional: '(optionnel)',
      },
      message: {
        label: 'Message',
        placeholder: 'Décrivez votre projet ou posez-nous une question',
      },
      submit: 'Envoyer le message',
      submitting: 'Envoi en cours...',
      success: 'Votre message a été envoyé avec succès! Nous vous contacterons bientôt.',
      error: 'Une erreur est survenue. Veuillez réessayer plus tard.',
    },
  },
  footer: {
    about: 'Entreprise spécialisée en terrassement, excavation, nivellement et déneigement à Québec et ses environs.',
    quickLinks: 'Liens rapides',
    ourServices: 'Nos services',
    contactUs: 'Contactez-nous',
    copyright: 'Tous droits réservés.',
    privacy: 'Politique de confidentialité',
    terms: 'Conditions d\'utilisation',
  },
};

// English translations
export const enTranslations: Translations = {
  header: {
    home: 'Home',
    about: 'About',
    services: 'Services',
    gallery: 'Project Gallery',
    contactBtn: 'Contact Us',
  },
  hero: {
    title: 'Vaudreuil Multiservices Inc.',
    subtitle: 'Landscaping • Excavation • Leveling • Snow Removal',
    services: 'Landscaping • Excavation • Leveling • Snow Removal',
    experience: '20+ years of experience • Residential projects',
    cta: 'Request a quote',
  },
  about: {
    title: 'About Us',
    history: {
      title: 'Our History',
      content: 'Founded in 2022, Vaudreuil Multiservices is a family business with over 20 years of experience in excavation and landscaping.',
    },
    team: {
      title: 'Our Team',
      content: 'Our team consists of qualified professionals with extensive experience in construction and landscaping.',
    },
    commitment: {
      title: 'Our Commitment',
      content: 'We are committed to providing high-quality services, respecting deadlines and budgets, to ensure total customer satisfaction.',
    },
  },
  services: {
    title: 'Our Services',
    items: {
      excavation: {
        title: 'Excavation',
        description: 'Excavation services for residential projects and peaceful memorial garden landscapes.',
      },
      landscaping: {
        title: 'Landscaping',
        description: 'Landscaping and earthworks to beautify your property.',
      },
      leveling: {
        title: 'Leveling',
        description: 'Land leveling services for a stable foundation.',
      },
      snowRemoval: {
        title: 'Snow Removal',
        description: 'Residential snow removal services during winter.',
      },
      drainage: {
        title: 'Drainage',
        description: 'Land drainage solutions for better management of surface water and prevention of water accumulation on your property.',
      },
      installation: {
        title: 'Installation',
        description: 'Professional installation of concrete slabs for parking lots, sidewalks, and more.',
      },
    },
  },
  gallery: {
    title: 'Project Gallery',
    categories: {
      all: 'All',
      excavation: 'Excavation',
      landscaping: 'Landscaping',
      preparation: 'Site Prep',
      concrete: 'Concrete'
    },
    noItems: 'No projects in this category',
    items: {
      residentialExcavation: {
        title: 'Residential Excavation',
        description: 'Excavation project for a new residential construction in Vaudreuil.'
      },
      poolExcavation: {
        title: 'Pool Excavation',
        description: 'Site preparation and excavation for residential swimming pool installation.'
      },
      sitePreperation: {
        title: 'Site Preparation',
        description: 'Complete site preparation for a new multi-residential development.'
      },
      landscaping: {
        title: 'Landscaping',
        description: 'Complete landscaping project for a luxury property in Hudson.'
      },
      concrete: {
        title: 'Concrete Work',
        description: 'Foundation pouring and concrete work for a residential project.'
      },
      memorialSpace: {
        title: 'Memorial Garden Space',
        description: 'Excavation and landscaping work for a peaceful memorial garden.'
      }
    }
  },
  testimonials: {
    title: 'Testimonials',
    verified: 'Verified Client',
    writeReview: 'Write a Review'
  },
  contact: {
    title: 'Contact Us',
    subtitle: 'Feel free to contact us for any questions or quote requests.',
    info: {
      title: 'Contact Information',
      address: {
        label: 'Address',
        value: '1772 rue de la Petite-Oasis, Quebec City, QC',
      },
      phone: {
        label: 'Phone',
        value: '(418) 907-9922',
      },
      email: {
        label: 'Email',
        value: 'vaudreuilmultiservices@gmail.com',
      },
      hours: {
        label: 'Working Hours',
        weekdays: 'Mon - Fri: 8:00 AM - 5:00 PM',
        saturday: 'Sat: 9:00 AM - 12:00 PM',
        sunday: 'Sun: Closed',
      },
    },
    form: {
      title: 'Send Us a Message',
      name: {
        label: 'Full Name',
        placeholder: 'Your full name',
      },
      email: {
        label: 'Email',
        placeholder: 'Your email address',
      },
      phone: {
        label: 'Phone',
        placeholder: 'Your phone number',
        optional: '(optional)',
      },
      address: {
        label: 'Address',
        placeholder: 'Your address',
        optional: '(optional)',
      },
      service: {
        label: 'Service',
        placeholder: 'Select a service',
        optional: '(optional)',
      },
      message: {
        label: 'Message',
        placeholder: 'Describe your project or ask us a question',
      },
      submit: 'Send Message',
      submitting: 'Sending...',
      success: 'Your message has been sent successfully! We will contact you soon.',
      error: 'An error occurred. Please try again later.',
    },
  },
  footer: {
    about: 'Residential landscaping, excavation, leveling, and snow removal services in Quebec City and surrounding areas.',
    quickLinks: 'Quick Links',
    ourServices: 'Our Services',
    contactUs: 'Contact Us',
    copyright: 'All rights reserved.',
    privacy: 'Privacy Policy',
    terms: 'Terms of Use',
  },
};
